import { type CheckedState } from '@radix-ui/react-checkbox';
import React, { Dispatch, FC, useEffect, useRef, useState } from 'react';
import { Checkbox } from "../../ui/checkbox-shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip-shadcn";
import HelpIcon from "../../../assets/icons/info.svg";
import { CalculatorRadio } from "../../calculator/styles";
import { RadioLabel } from './main';
import {
  DraperyItemDetailsFields,
  DraperyItemDetailsFieldsAction,
  bundleSelectionsInitialState,
} from '../../../redux/modules/my-shop/drapery-item-details-fields-reducer';

interface BundlingSettingsProps {
  title: string;
  subTitle: string;
  draperyItemDetailsFields: DraperyItemDetailsFields;
  dispatchItemDetails: Dispatch<DraperyItemDetailsFieldsAction>;
  isEstimateSettingsReadOnly?: boolean;
}

const BundlingSettings: FC<BundlingSettingsProps> = ({
  title,
  subTitle,
  draperyItemDetailsFields,
  dispatchItemDetails,
  isEstimateSettingsReadOnly,
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipPortalContainer, setTooltipPortalContainer] = useState<HTMLElement | null>(null);
  const bundleSelections = {
    ...bundleSelectionsInitialState,
    ...(draperyItemDetailsFields.bundleSelections || {}),
  };
  const isBundlingSelected = Boolean(draperyItemDetailsFields.isBundlingSelected);
  const bundlingOptionsRef = useRef<HTMLDivElement | null>(null);
  const [shouldScrollToOptions, setShouldScrollToOptions] = useState(false);

  const handleBundleSelectionChange =
    (key: keyof typeof bundleSelections) =>
      (checked: CheckedState) => {
        if (isEstimateSettingsReadOnly) {
          return;
        }

        dispatchItemDetails({
          type: 'updateFormFields',
          formFieldUpdates: {
            bundleSelections: {
              ...bundleSelections,
              [key]: Boolean(checked),
            },
          },
        });
      };

  const handleBundlingToggle = (selected: boolean) => {
    if (isEstimateSettingsReadOnly) {
      return;
    }

    if (selected) {
      setShouldScrollToOptions(true);
    } else {
      setShouldScrollToOptions(false);
    }
    dispatchItemDetails({
      type: 'updateFormFields',
      formFieldUpdates: {
        isBundlingSelected: selected,
        bundleSelections,
      },
    });
  };
  const tooltipTriggerRef = useRef<HTMLButtonElement | null>(null);
  const tooltipContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isTooltipOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;

      if (
        (tooltipTriggerRef.current && tooltipTriggerRef.current.contains(target)) ||
        (tooltipContentRef.current && tooltipContentRef.current.contains(target))
      ) {
        return;
      }

      setIsTooltipOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isTooltipOpen]);

  const scrollOptionsIntoView = () => {
    const optionsNode = bundlingOptionsRef.current;
    if (!optionsNode) return;

    const scrollableContainer =
      (optionsNode.closest('.react-tabs__tab-panel') as HTMLElement | null) ||
      (optionsNode.closest('.modal-element') as HTMLElement | null);

    if (scrollableContainer) {
      const containerRect = scrollableContainer.getBoundingClientRect();
      const optionsRect = optionsNode.getBoundingClientRect();
      const nextScrollTop = optionsRect.top - containerRect.top + scrollableContainer.scrollTop - 12;

      scrollableContainer.scrollTo({
        top: nextScrollTop < 0 ? 0 : nextScrollTop,
        behavior: 'smooth',
      });
      return;
    }

    optionsNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (isBundlingSelected && shouldScrollToOptions) {
      scrollOptionsIntoView();
      setShouldScrollToOptions(false);
    } else if (!isBundlingSelected && shouldScrollToOptions) {
      setShouldScrollToOptions(false);
    }
  }, [isBundlingSelected, shouldScrollToOptions]);

  useEffect(() => {
    setTooltipPortalContainer(
      (tooltipTriggerRef.current?.closest('.modal-element') as HTMLElement | null) || null
    );
  }, []);

  return (
    <div className="mt-4">
      <div className="mb-5 flex items-center gap-2 h-auto">
        <span className="font-bold text-2xl">{title}</span>
        <Tooltip
          open={isTooltipOpen}
        >
          <TooltipTrigger asChild ref={tooltipTriggerRef}>
            <button
              type="button"
              className="flex h-5 w-5 items-center justify-center bg-white transition-colors"
              aria-label="Bundling details"
              aria-expanded={isTooltipOpen}
              onClick={() => setIsTooltipOpen(true)}
            >
              <HelpIcon className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            className="relative max-w-[320px] whitespace-pre-wrap rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-4 pr-9 text-[14px] leading-[1.6] text-[#111827] shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
            arrowClassName="fill-white"
            sideOffset={8}
            portalContainer={tooltipPortalContainer}
            ref={tooltipContentRef}
          >
            <button
              type="button"
              className="absolute right-3 top-3 text-[#6B7280] hover:text-[#111827]"
              onClick={() => setIsTooltipOpen(false)}
              aria-label="Close bundling details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" data-lov-id="src/components/EditDraperyDialog.tsx:553:24" data-lov-name="X" data-component-path="src/components/EditDraperyDialog.tsx" data-component-line="553" data-component-file="EditDraperyDialog.tsx" data-component-name="X" data-component-content="%7B%22className%22%3A%22w-3%20h-3%22%7D"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
            </button>
            The estimate description and price for selected components will be combined into one line
            with fabrication for customer-facing estimates. Each of the bundled components will be
            displayed as an itemized part of the bundle internally for QuickBooks accounting and
            reporting purposes. Components not selected will be listed as separate line items on the
            customer-facing estimate.
          </TooltipContent>
        </Tooltip>
      </div>
      <p className="text-sm" style={{ color: '#656F77' }}>{subTitle}</p>

      <div className="flex flex-col mt-3">
        <RadioLabel className="mb-2">
          <CalculatorRadio
            id="no-bundling"
            name="bundling-settings"
            type="radio"
            value="none"
            checked={!isBundlingSelected}
            onChange={() => handleBundlingToggle(false)}
            className="cursor-pointer"
            disabled={isEstimateSettingsReadOnly}
          />
          No Bundling
        </RadioLabel>

        <RadioLabel className="mb-2">
          <CalculatorRadio
            id="bundle-components"
            name="bundling-settings"
            type="radio"
            value="bundle"
            checked={isBundlingSelected}
            onChange={() => handleBundlingToggle(true)}
            className="cursor-pointer"
            disabled={isEstimateSettingsReadOnly}
          />
          Bundle Components <span className="ml-[5px] text-m font-bold">(Only available for QBO Plus and Advanced plans and requires additional setup in QuickBooks.)</span><a className="ml-[5px] link-underline-invert" target="_blank" rel="noopener noreferrer" href="https://quipa.crisp.help/en/article/qbo-bundling-setup-h3lrqd/?bust=1764782701259">QBO Bundling Setup</a>
        </RadioLabel>

        {isBundlingSelected && <p className="text-sm" style={{ color: '#656F77' }}>Select which components you want to bundle with the fabrication price. Components that are not selected will be listed as a separate line item.</p>}

        {isBundlingSelected && (
          <div
            className="mt-2 space-y-3 ml-[25px]"
            ref={bundlingOptionsRef}
          >
            <label
              htmlFor="bundle-main-fabric"
              className="flex cursor-pointer items-center gap-2"
            >
              <Checkbox
                id="bundle-main-fabric"
                checked={bundleSelections.mainFabric}
                onCheckedChange={handleBundleSelectionChange('mainFabric')}
                disabled={isEstimateSettingsReadOnly}
              />
              <span>Main Fabric</span>
            </label>

            <label
              htmlFor="bundle-linings"
              className="flex cursor-pointer items-center gap-2"
            >
              <Checkbox
                id="bundle-linings"
                checked={bundleSelections.linings}
                onCheckedChange={handleBundleSelectionChange('linings')}
                disabled={isEstimateSettingsReadOnly}
              />
              <span>Linings</span>
            </label>


            <label
              htmlFor="bundle-embellishments"
              className="flex cursor-pointer items-center gap-2"
            >
              <Checkbox
                id="bundle-embellishments"
                checked={bundleSelections.embellishments}
                onCheckedChange={handleBundleSelectionChange('embellishments')}
                disabled={isEstimateSettingsReadOnly}
              />
              <span>Embellishments</span>
            </label>

            <label
              htmlFor="bundle-headrail"
              className="flex cursor-pointer items-center gap-2"
            >
              <Checkbox
                id="bundle-headrail"
                checked={bundleSelections.headrail}
                onCheckedChange={handleBundleSelectionChange('headrail')}
                disabled={isEstimateSettingsReadOnly}
              />
              <span>Headrail</span>
            </label>

            <label
              htmlFor="bundle-fees"
              className="flex cursor-pointer items-center gap-2"
            >
              <Checkbox
                id="bundle-fees"
                checked={bundleSelections.fees}
                onCheckedChange={handleBundleSelectionChange('fees')}
                disabled={isEstimateSettingsReadOnly}
              />
              <span>Fees</span>
            </label>

            <label
              htmlFor="bundle-shippingAndHandling"
              className="flex cursor-pointer items-center gap-2"
            >
              <Checkbox
                id="bundle-shippingAndHandling"
                checked={bundleSelections.shippingAndHandling}
                onCheckedChange={handleBundleSelectionChange('shippingAndHandling')}
              />
              <span>Shipping & Handling</span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default BundlingSettings;
